import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import type { ExportBrowseSearch } from '~/routes/_authenticated/_tenant/exports/$sourceWorkflowId';

/**
 * The full-page export view's working state, read from and written to the
 * route's search params.
 *
 * The URL is the single source of truth here — there is no mirrored `useState`
 * to fall out of sync. Narrowing a 34k-product run takes minutes of choices,
 * and losing them to a reload (or being unable to hand a colleague the exact
 * view) is the real cost this page exists to remove.
 *
 * Writes use `replace` so every filter tick does not become a Back-button
 * stop, and take a patch so callers never have to know the other params.
 */
export function useBrowseUrlState() {
  // Two spellings of one route: `useSearch` matches on the route ID, `useNavigate`
  // on its path — the router types accept nothing else for each.
  const search = useSearch({ from: '/_authenticated/_tenant/exports/$sourceWorkflowId' });
  const navigate = useNavigate({ from: '/exports/$sourceWorkflowId' });

  const patch = useCallback(
    (changes: Partial<ExportBrowseSearch>) => {
      void navigate({
        search: (prev) => {
          // `undefined` values are dropped so a cleared filter leaves the URL
          // rather than lingering as `?minScore=`.
          const merged: Record<string, unknown> = { ...prev, ...changes };
          return Object.fromEntries(
            Object.entries(merged).filter(([, value]) => value !== undefined),
          ) as ExportBrowseSearch;
        },
        replace: true,
      });
    },
    [navigate],
  );

  return { search, patch };
}
